import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";

import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import ContactForm from "../../../components/common/ContactForm";
import { useAbout } from "../../../services/aboutService";
import { useTeamMembers } from "../../../services/teamMemberService";
import { useCoreValues } from "../../../services/coreValueService";

const About = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Fetch about data from API
  const {
    data: aboutData,
    isLoading: aboutLoading,
    error: aboutError,
  } = useAbout(currentLanguage);

  // Fetch team members from API
  const {
    data: teamMembersData,
    isLoading: teamLoading,
    error: teamError,
  } = useTeamMembers();

  // Fetch core values from API
  const {
    data: coreValuesData,
    isLoading: coreValuesLoading,
    error: coreValuesError,
  } = useCoreValues({
    locale: currentLanguage,
  });

  // Trigger animations when data loads
  useEffect(() => {
    if (
      !aboutLoading &&
      !teamLoading &&
      !coreValuesLoading &&
      aboutData &&
      teamMembersData &&
      coreValuesData
    ) {
      setIsDataLoaded(true);
    }
  }, [
    aboutLoading,
    teamLoading,
    coreValuesLoading,
    aboutData,
    teamMembersData,
    coreValuesData,
  ]);

  // Loading state
  if (aboutLoading || teamLoading || coreValuesLoading) {
    return (
      <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
        <Header />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  // Error state
  if (aboutError || teamError || coreValuesError) {
    return (
      <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
        <Header />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4 text-red-600 dark:text-red-400">
              Something went wrong
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We couldn't load the about page. Please try again later.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      <Header />
      <main>
        {/* Hero Section */}
        <section
          className="relative min-h-[85vh] flex items-center overflow-hidden bg-gradient-to-br from-emerald-600 via-stone-600 to-emerald-700 text-white"
          style={{
            backgroundImage: aboutData?.hero_image
              ? `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${aboutData.hero_image})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8 }}
                className="mb-8"
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md text-white rounded-full text-sm font-semibold border border-white/30 shadow-lg">
                  <span className="text-lg">🤝</span>
                  <span>Our Community</span>
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight tracking-tight"
              >
                {aboutData?.hero_title || t("about.title")}
                <span className="block mt-3 text-3xl sm:text-4xl md:text-5xl lg:text-6xl bg-gradient-to-r from-emerald-300 via-stone-300 to-emerald-200 bg-clip-text text-transparent">
                  {t("about.titleAccent", "Building Together")}
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg md:text-xl lg:text-2xl opacity-95 font-light max-w-3xl mx-auto leading-relaxed mb-12"
              >
                {aboutData?.hero_description || t("about.story.content")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-wrap justify-center gap-6"
              >
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/projects")}
                  className="px-8 py-4 bg-white text-emerald-600 font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Our Projects
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/events")}
                  className="px-8 py-4 bg-white/10 backdrop-blur-md text-white border-2 border-white/30 font-bold text-lg rounded-2xl hover:bg-white/20 transition-all duration-300"
                >
                  Join Us
                </motion.button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={isDataLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-6"
              >
                <div>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded-full text-sm font-semibold mb-6">
                    <span className="text-lg">📖</span>
                    <span>Our Story</span>
                  </span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight">
                  {aboutData?.story_title || t("about.story.title")}
                  <span className="block mt-2 text-3xl md:text-4xl lg:text-5xl bg-gradient-to-r from-emerald-600 to-stone-600 bg-clip-text text-transparent">
                    {t("about.story.titleAccent", "Our Journey")}
                  </span>
                </h2>
                <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                  {aboutData?.story_content || t("about.story.content")}
                </p>
              </motion.div>

              {/* Right Image */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={isDataLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="relative"
              >
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={
                      aboutData?.hero_image ||
                      "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&h=600&fit=crop"
                    }
                    alt="Djichami Community"
                    className="w-full h-96 lg:h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

                  {/* Floating stat card */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={isDataLoaded ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="absolute bottom-6 left-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50 max-w-xs"
                  >
                    <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-2">
                      500+
                    </div>
                    <div className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                      Community Members
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* About Section Body */}
        {aboutData?.section_body && (
          <section className="relative py-4 px-4 sm:px-6 lg:px-8 overflow-hidden bg-white dark:bg-gray-900 transition-colors">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8 }}
                className="prose prose-lg dark:prose-invert max-w-none prose-emerald"
              >
                <div
                  dangerouslySetInnerHTML={{ __html: aboutData.section_body }}
                />
              </motion.div>
            </div>
          </section>
        )}

        {/* Mission and Vision */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-emerald-50/50 via-stone-50/30 to-emerald-50/50 dark:from-gray-800 dark:via-emerald-900/10 dark:to-gray-900">
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-emerald-800 dark:text-emerald-300 rounded-full text-sm font-semibold border border-emerald-200 dark:border-emerald-800 shadow-lg mb-6">
                <span className="text-lg">🎯</span>
                <span>Our Purpose</span>
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
                {t("about.missionVision.title", "Mission & Vision")}
                <span className="block mt-2 text-3xl md:text-4xl lg:text-5xl bg-gradient-to-r from-emerald-600 via-stone-600 to-emerald-700 bg-clip-text text-transparent">
                  {t("about.missionVision.titleAccent", "Our Goals")}
                </span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {/* Mission Card */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-stone-500 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 h-full">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-stone-600 rounded-2xl mb-6 shadow-lg">
                    <span className="text-3xl">🎯</span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-extrabold mb-4 text-gray-900 dark:text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-stone-600 transition-all duration-300">
                    {aboutData?.mission_title || t("about.mission.title")}
                  </h3>
                  <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                    {aboutData?.mission_content || t("about.mission.content")}
                  </p>
                  <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br from-emerald-500 to-stone-500 opacity-10 group-hover:opacity-20 rounded-full blur-2xl transition-opacity duration-500"></div>
                </div>
              </motion.div>

              {/* Vision Card */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-stone-500 to-emerald-500 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 h-full">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-stone-500 to-emerald-600 rounded-2xl mb-6 shadow-lg">
                    <span className="text-3xl">👁️</span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-extrabold mb-4 text-gray-900 dark:text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-stone-600 group-hover:to-emerald-600 transition-all duration-300">
                    {aboutData?.vision_title || t("about.vision.title")}
                  </h3>
                  <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                    {aboutData?.vision_content || t("about.vision.content")}
                  </p>
                  <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br from-stone-500 to-emerald-500 opacity-10 group-hover:opacity-20 rounded-full blur-2xl transition-opacity duration-500"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-emerald-50 via-stone-50/50 to-emerald-50 dark:from-gray-900 dark:via-emerald-900/10 dark:to-stone-900/10">
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-20"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={
                  isDataLoaded
                    ? { scale: 1, opacity: 1 }
                    : { scale: 0.9, opacity: 0 }
                }
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-block mb-6"
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-emerald-800 dark:text-emerald-300 rounded-full text-sm font-semibold border border-emerald-200 dark:border-emerald-800 shadow-lg">
                  <span className="text-lg">💎</span>
                  <span>{t("about.coreValues.badge", "Our Foundation")}</span>
                </span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
              >
                {t("about.coreValues.title", "Our Core Values")}
                <span className="block mt-2 bg-gradient-to-r from-emerald-600 via-stone-600 to-emerald-700 bg-clip-text text-transparent">
                  {t("about.coreValues.titleAccent", "What Guides Us")}
                </span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto"
              >
                {t(
                  "about.coreValues.description",
                  "The principles that guide everything we do and shape our community's future."
                )}
              </motion.p>
            </motion.div>

            {/* Values Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
              {coreValuesData?.core_values
                ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((value, index) => {
                  const gradient = value.gradient || "from-emerald-500 to-stone-600";
                  return (
                    <motion.div
                      key={value.id}
                      initial={{ opacity: 0, y: 50 }}
                      animate={
                        isDataLoaded
                          ? { opacity: 1, y: 0 }
                          : { opacity: 0, y: 50 }
                      }
                      transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
                      whileHover={{ y: -12, scale: 1.03 }}
                      className="group relative"
                    >
                      {/* Floating gradient effect */}
                      <div
                        className={`absolute -inset-1 bg-gradient-to-r ${gradient} rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500`}
                      ></div>

                      {/* Card */}
                      <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 h-full">
                        {/* Icon with gradient background */}
                        <motion.div
                          whileHover={{ rotate: 5, scale: 1.1 }}
                          className={`inline-flex items-center justify-center w-20 h-20 lg:w-24 lg:h-24 rounded-2xl bg-gradient-to-br ${gradient} mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300`}
                        >
                          {value.icon ? (
                            <i
                              className={value.icon}
                              style={{ fontSize: "48px", color: "white" }}
                              aria-hidden="true"
                            ></i>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="48"
                              height="48"
                              fill="currentColor"
                              viewBox="0 0 256 256"
                              className="text-white"
                            >
                              <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,0-8-8,16,16,0,0,1,0-32,8,8,0,0,0,8-8,16,16,0,0,1,16-16,8,8,0,0,1,8,8V168A8,8,0,0,1,144,176Z" />
                            </svg>
                          )}
                        </motion.div>

                        {/* Content */}
                        <h3 className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-stone-600 transition-all duration-300">
                          {value.title || `Core Value ${index + 1}`}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6 text-base lg:text-lg">
                          {value.description || ""}
                        </p>

                        {/* Decorative gradient circle */}
                        <div
                          className={`absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br ${gradient} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity duration-500`}
                        ></div>
                      </div>
                    </motion.div>
                  );
                }) || []}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-stone-50/50 via-emerald-50/30 to-stone-50/50 dark:from-gray-800 dark:via-stone-900/10 dark:to-gray-900">
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-20"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-stone-800 dark:text-stone-300 rounded-full text-sm font-semibold border border-stone-200 dark:border-stone-800 shadow-lg mb-6">
                <span className="text-lg">👥</span>
                <span>{t("about.team.badge", "Meet the Team")}</span>
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
                {t("about.team.title")}
                <span className="block mt-2 text-3xl md:text-4xl lg:text-5xl bg-gradient-to-r from-stone-600 via-emerald-600 to-stone-700 bg-clip-text text-transparent">
                  {t("about.team.titleAccent", "Our Leaders")}
                </span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
                {t("about.team.description")}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {teamMembersData?.team_members?.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  whileHover={{ y: -12, scale: 1.05 }}
                  className="group relative"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-stone-500 via-emerald-500 to-stone-600 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>
                  <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 text-center">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-gradient-to-r from-stone-500 to-emerald-500 opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-500"></div>
                      <img
                        src={
                          member.image_url ||
                          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
                        }
                        alt={member.name}
                        className="w-48 h-48 mx-auto object-cover border-4 border-white dark:border-gray-700 shadow-xl relative z-10 group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-xl font-extrabold mb-2 text-gray-900 dark:text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-stone-600 group-hover:to-emerald-600 transition-all duration-300">
                      {member.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 font-semibold">
                      {member.role}
                    </p>
                  </div>
                </motion.div>
              )) || []}
            </div>
          </div>
        </section>

        {/* Contact CTA Section */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-gray-50 via-emerald-50/30 to-stone-50/50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-900">
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={isDataLoaded ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-6"
              >
                <span className="text-5xl">💬</span>
              </motion.div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
                {aboutData?.contact_title || t("about.contact.title")}
                <span className="block mt-2 text-3xl md:text-4xl lg:text-5xl bg-gradient-to-r from-emerald-600 to-stone-600 bg-clip-text text-transparent">
                  {t("about.contact.titleAccent", "Get in Touch")}
                </span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-8 max-w-2xl mx-auto">
                {aboutData?.contact_description || t("contact.helpDescription")}
              </p>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const contactSection = document.querySelector("section[class*='ContactForm']");
                  contactSection?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                {t("about.contact.button", "Contact Us")}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </motion.button>
            </motion.div>
          </div>
        </section>
      </main>
      <ContactForm
        mapEmbedUrl={aboutData?.contact_map_url}
        address={aboutData?.contact_address}
        email={aboutData?.contact_email}
        phone={aboutData?.contact_phone}
      />
      <Footer />
      <BackToTop />
    </div>
  );
};

export default About;